import React from 'react';
import { PayPalButtons, PayPalScriptProvider, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { API_BASE_URL, PAYPAL_CLIENT_ID, PLATFORM_BANK_ACCOUNT, PLATFORM_FEE_PERCENT, PLATFORM_WALLET_ADDRESS } from '@/config';

// Props are generic to support all checkout types, with token added for auth.
interface PayPalFiatCheckoutProps {
  amount: number;
  currency: string;
  description: string;
  userId?: string; 
  token: string | null;
  onSuccess: (data: any) => void;
  onError: (message: string) => void;
  marketplaceItemId?: string;
  quantity?: number;
}

const CheckoutForm: React.FC<PayPalFiatCheckoutProps> = ({
  amount,
  currency,
  description,
  userId,
  token,
  onSuccess,
  onError,
  marketplaceItemId,
  quantity,
}) => {
    const [{ isPending }] = usePayPalScriptReducer();

    const getAuthHeaders = () => {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      return headers;
    };

    return (
        <>
            {isPending && (
                <div className="flex justify-center items-center p-4">
                    <div className="w-8 h-8 border-2 border-kala-700 border-t-kala-secondary rounded-full animate-spin"></div>
                    <p className="ml-3 text-kala-300">Loading PayPal...</p>
                </div>
            )}
            <div style={{ display: isPending ? 'none' : 'block' }}>
                <PayPalButtons
                    style={{ layout: 'vertical' }}
                    createOrder={async () => {
                      // FINAL, CORRECTED, SURGICAL FIX:
                      // This robustly handles all server responses by checking for HTTP errors *before* attempting to parse the JSON body.
                      const response = await fetch(`${API_BASE_URL}/api/fiat-transactions/paypal/create-order`, {
                        method: 'POST',
                        headers: getAuthHeaders(),
                        credentials: 'include',
                        body: JSON.stringify({
                          amount: amount.toFixed(2),
                          currency,
                          description,
                          userId,
                          marketplaceItemId,
                          quantity,
                        }),
                      });

                      if (!response.ok) {
                        let errorMessage = `Server error: ${response.status}`;
                        try {
                            // Try to get a more specific error message from the JSON body.
                            const errorData = await response.json();
                            errorMessage = errorData.error || errorMessage;
                        } catch (e) {
                            // Ignore if the body isn't JSON; the HTTP status is the best error we have.
                        }
                        // This throw is critical. It rejects the promise, and PayPal's onError catches it.
                        throw new Error(errorMessage);
                      }

                      const data = await response.json();

                      if (!data.orderId) {
                        throw new Error('Server returned a success status but did not provide an order ID.');
                      }

                      return data.orderId;
                    }}
                      onApprove={async (data) => {
                        try {
                          const response = await fetch(`${API_BASE_URL}/api/fiat-transactions/paypal/capture-order`, {
                            method: 'POST',
                            headers: getAuthHeaders(),
                            credentials: 'include',
                            body: JSON.stringify({ orderId: data.orderID, description, userId }),
                          });
                          const result = await response.json();
                          if (!result.success) {
                            throw new Error(result.error || 'Failed to capture payment.');
                          }
                          onSuccess(result);
                        } catch (error) {
                            onError(error instanceof Error ? error.message : 'An unknown error occurred during capture.');
                        }
                      }}
                      onError={(err) => {
                        // This is the correct, safe place to handle state updates for any failure in createOrder.
                        onError(err.message);
                      }}
                />
            </div>
        </>
    );
};

const PayPalFiatCheckout: React.FC<PayPalFiatCheckoutProps> = (props) => {
  if (!PAYPAL_CLIENT_ID) {
    return (
      <div className="text-xs text-yellow-300 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
        <strong>PayPal Checkout is not available.</strong>
        <p className="mt-1">The <code>VITE_PAYPAL_CLIENT_ID</code> environment variable is missing.</p>
      </div>
    );
  }

  const { amount, currency } = props;
  const platformFee = ((amount * PLATFORM_FEE_PERCENT) / 100).toFixed(2);
  const netAfterFee = (amount - Number(platformFee)).toFixed(2);

  return (
    <div className="space-y-3">
      <div className="text-xs bg-kala-900/40 border border-kala-700 rounded-lg p-3 text-kala-300">
        <p>
          Platform fee: <span className="font-bold">{PLATFORM_FEE_PERCENT}%</span> ({platformFee} {currency})
        </p>
        <p>
          Net to counterparty: <span className="font-bold">{netAfterFee} {currency}</span>
        </p>
        <p className="mt-1 text-kala-400">
          Settlement targets: wallet={PLATFORM_WALLET_ADDRESS}, bank={PLATFORM_BANK_ACCOUNT}
        </p>
      </div>

      <PayPalScriptProvider 
        key={currency} 
        options={{ 'client-id': PAYPAL_CLIENT_ID, currency, intent: 'capture', components: 'buttons' }}>
        <CheckoutForm {...props} />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalFiatCheckout;
